<script>
import {computed, defineComponent, onBeforeMount, reactive, ref} from "vue";
import {useStore} from "vuex";

import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from 'primevue/textarea';
import Dropdown from "primevue/dropdown";
import MainCard from "@/components/cards/MainCard";
import Breadcrumb from "@/components/Breadcrumb";
import ButtonSuccess from "@/components/buttons/ButtonSuccess";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Skeleton from "primevue/skeleton"
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {required} from "@/utils/i18n-validators";
import {useVuelidate} from "@vuelidate/core";
import {useApiCall} from "@/composables/useApiCall";
import {
  getVacanciesItemApiCall,
  getDepartmentsVacanciesApiCall,
  getLocationsVacanciesApiCall,
  createVacanciesApiCall,
  updateVacanciesApiCall,
  deleteVacanciesApiCall,
} from "@/services/vacancies";
import Checkbox from "primevue/checkbox";
import Editor from "primevue/editor";

export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {
    Editor,
    Checkbox,
    ButtonSuccess,
    Button,
    InputText,
    MainCard,
    Breadcrumb,
    Textarea,
    ConfirmationModal,
    Skeleton,
    Dropdown,
  },
  setup() {
    const {t} = useI18n();

    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const positionField = computed(() => !route.params.id ? 'position_ru' : `position_${lang.value}`);
    const scheduleField = computed(() => !route.params.id ? 'schedule_ru' : `schedule_${lang.value}`);
    const descriptionField = computed(() => !route.params.id ? 'description_ru' : `description_${lang.value}`);
    const nameField = computed(() => !route.params.id ? 'name_ru' : `name_${lang.value}`);
    const addressField = computed(() => !route.params.id ? 'address_ru' : `address_${lang.value}`);

    const router = useRouter()
    const route = useRoute()
    const errors = ref(null)
    const breadcrumbs = ref([]);
    const form = reactive({
      fields: {
        id: '',
        department_id: null,
        location_id: null,
        position_ru: '',
        position_en: '',
        position_ch: '',
        description_ru: '',
        description_en: '',
        description_ch: '',
        salary: '',
        schedule_ru: '',
        schedule_en: '',
        schedule_ch: '',
      }
    })
    const rules = computed(() => ({
      [positionField.value]: {required},
      department_id: {required},
      location_id: {required},
    }));
    const v$ = useVuelidate(rules, form.fields);
    const visibleModal = ref(false);
    const isContentLoading = ref(true)
    const isUpdateLoading = computed(() =>
        isContentUpdateLoading.value ||
        isContentAddLoading.value
    )

    const {
      data: contentData,
      error: contentError,
      executeApiCall: getContentAction,
    } = useApiCall(getVacanciesItemApiCall, true)

    const {
      data: departmentsData,
      error: departmentsError,
      executeApiCall: getDepartmentsAction,
    } = useApiCall(getDepartmentsVacanciesApiCall, true)

    const {
      data: locationsData,
      error: locationsError,
      executeApiCall: getLocationsAction,
    } = useApiCall(getLocationsVacanciesApiCall, true)

    const {
      isLoading: isContentUpdateLoading,
      data: contentUpdateData,
      error: contentUpdateError,
      executeApiCall: updateContentAction,
    } = useApiCall(updateVacanciesApiCall, true)

    const {
      isLoading: isContentAddLoading,
      data: contentAddData,
      error: contentAddError,
      executeApiCall: addContentAction,
    } = useApiCall(createVacanciesApiCall, true)

    const {
      isLoading: isContentDeleteLoading,
      data: contentDeleteData,
      error: contentDeleteError,
      executeApiCall: deleteContentAction,
    } = useApiCall(deleteVacanciesApiCall, true)

    const changeConfirmationStateModal = () => {
      visibleModal.value = !visibleModal.value
    };

    const toggleDestroyPartner = async () => {
      try {
        await deleteContentAction({id: route.params.id});
        if (contentDeleteData.value) {
          await router.push({name: 'website', query: {tab: 'vacancies'}});
        }
      } catch (e) {
        console.error(e);
      }
    };

    const update = async () => {
      const result = await v$.value.$validate()
      errors.value = null
      if (result) {
        try {
          const prepareData = {
            location_id: form.fields.location_id,
            department_id: form.fields.department_id,
            position_ru: form.fields.position_ru,
            position_en: form.fields.position_en ?? null,
            position_ch: form.fields.position_ch ?? null,
            salary: form.fields.salary,
            schedule_ru: form.fields.schedule_ru ?? null,
            schedule_en: form.fields.schedule_en ?? null,
            schedule_ch: form.fields.schedule_ch ?? null,
            description_ru: form.fields.description_ru ?? null,
            description_en: form.fields.description_en ?? null,
            description_ch: form.fields.description_ch ?? null,
          }
          if (route.params.id) {
            await updateContentAction({
              data: prepareData,
              id: route.params.id,
            })
          } else {
            await addContentAction(prepareData)
          }
          if (contentUpdateData.value || contentAddData.value) {
            await router.push({name: 'website', query: {tab: 'vacancies'}});
          }
        } catch (e) {
          console.error(e)
          if (contentUpdateError.value) {
            errors.value = contentUpdateError.value.data
          }
          if (contentAddError.value) {
            errors.value = contentAddError.value.data
          }
        }
      }
    }
    const departments = computed(() => departmentsData.value?.data || []);
    const positions = computed(() => locationsData.value?.data || []);

    onBeforeMount(async () => {
      await getDepartmentsAction()
      await getLocationsAction()
      if (route.params.id) {
        try {
          await getContentAction({id: route.params.id})
          if (contentData.value?.data) {
            form.fields.id = contentData.value.data.id
            form.fields.location_id = contentData.value.data.location?.id || null
            form.fields.department_id = contentData.value.data.department?.id || null
            form.fields.position_ru = contentData.value.data.position_ru ?? ''
            form.fields.position_en = contentData.value.data.position_en ?? ''
            form.fields.position_ch = contentData.value.data.position_ch ?? ''
            form.fields.salary = contentData.value.data.salary ?? ''
            form.fields.schedule_ru = contentData.value.data.schedule_ru ?? ''
            form.fields.schedule_en = contentData.value.data.schedule_en ?? ''
            form.fields.schedule_ch = contentData.value.data.schedule_ch ?? ''
            form.fields.description_ru = contentData.value.data.description_ru ?? ''
            form.fields.description_en = contentData.value.data.description_en ?? ''
            form.fields.description_ch = contentData.value.data.description_ch ?? ''
          }
        } catch (e) {
          console.error(e)
        }
      }
      breadcrumbs.value = [
        {label: 'Сайт / Вакансии', router: {name: 'website', query: {tab: 'vacancies'}}},
        {label: contentData.value ? `Редактировать вакансию` : 'Создать вакансию'}
      ];
      isContentLoading.value = false
    });

    return {
      t,
      form,
      breadcrumbs,
      route,
      update,
      v$,
      errors,
      visibleModal,
      isContentDeleteLoading,
      isContentLoading,
      toggleDestroyPartner,
      changeConfirmationStateModal,
      isUpdateLoading,
      positionField,
      scheduleField,
      departments,
      positions,
      nameField,
      addressField,
      descriptionField,
    };
  }
});
</script>

<template>
  <ConfirmationModal v-model:visible="visibleModal">
    <template #header>
      Удалить вакансию
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить вакансию?</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="changeConfirmationStateModal"
                class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" :loading="isContentDeleteLoading" @click="toggleDestroyPartner"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="py-2 mb-3">
    <div class="flex justify-content-between align-items-center">
      <Breadcrumb :data="breadcrumbs" separator="/"/>
      <div class="flex flex-wrap gap-4">
        <button v-if="route.params.id" class="modals-item__delete-button ml-auto" @click="changeConfirmationStateModal">
          Удалить
        </button>
        <Button
            @click="update"
            :loading="!!isUpdateLoading"
            :label="route.params.id ? 'Сохранить изменения' : 'Создать'"
            class="modals-item__save-button ml-auto"
        />
      </div>
    </div>
  </section>

  <section class="py-2 mb-3">
    <Skeleton v-if="isContentLoading" height="154px"></Skeleton>
    <div v-else class="segments__list">
      <MainCard title="Должность">
        <span class="p-float-label w-full">
          <InputText
              :class="{'p-invalid': v$[positionField].$errors?.length || errors?.[positionField] }"
              id="position"
              class="w-full"
              v-model="form.fields[positionField]"
          />
          <label for="position">Наименование должности <i style="color: #E26B6B">*</i></label>
        </span>
        <span v-if="v$[positionField].$errors?.length || errors?.[positionField]" class="color-error mt-3">
          {{ errors?.[positionField] ? errors[positionField][0] : t(v$[positionField].$errors?.[0].$message) }}
        </span>
      </MainCard>
      <MainCard title="Зарплата">
        <span class="p-float-label w-full">
          <InputText
              :class="{'p-invalid': errors?.salary }"
              id="position"
              type="number"
              class="w-full"
              v-model="form.fields.salary"
          />
          <label for="position">Зарплата (₽/мес)</label>
        </span>
        <span v-if="errors?.salary" class="color-error mt-3">
          {{ errors.salary[0] }}
        </span>
      </MainCard>
      <MainCard title="График">
        <span class="p-float-label w-full">
          <InputText
              :class="{'p-invalid': errors?.[scheduleField] }"
              id="position"
              class="w-full"
              v-model="form.fields[scheduleField]"
          />
          <label for="position">График работы</label>
        </span>
        <span v-if="errors?.[scheduleField]" class="color-error mt-3">
          {{ errors[scheduleField][0] }}
        </span>
      </MainCard>
    </div>
  </section>
  <section v-if="!isContentLoading" class="py-2 mb-3">
    <MainCard title="Описание">
        <span class="p-float-label w-full">
            <Textarea
                v-model="form.fields[descriptionField]"
                :class="['w-full', 'p-inputtext', {'p-invalid': errors?.[descriptionField]}]"
                :rows="2"
                id="title"
            />
            <label for="title">Описание</label>
            </span>
        <span v-if="errors?.[descriptionField]" class="text-xs color-error">
            {{ errors[descriptionField][0] }}
          </span>
    </MainCard>
  </section>
  <section v-if="!isContentLoading" class="py-2 mb-3">
    <MainCard title="Локация">
      <span class="p-float-label w-full">
        <span class="p-float-label">
          <Dropdown
              inputId="location_id"
              v-model="form.fields.location_id"
              :options="positions"
              :optionLabel="addressField"
              optionValue="id"
              class="w-full border-radius-15"
          />
          <label for="location_id">Выберите локацию</label>
        </span>
        <span v-if="v$.location_id.$errors?.length || errors?.location_id" class="color-error mt-3">
          {{ errors?.location_id ? errors.location_id[0] : t(v$.location_id.$errors?.[0].$message) }}
        </span>
      </span>
    </MainCard>
  </section>
  <section v-if="!isContentLoading" class="py-2 mb-3">
    <MainCard title="Отдел">
      <span class="p-float-label w-full">
        <span class="p-float-label">
          <Dropdown
              inputId="department_id"
              disable=""
              v-model="form.fields.department_id"
              :options="departments"
              :optionLabel="nameField"
              optionValue="id"
              class="w-full border-radius-15"
          />
          <label for="department_id">Выберите отдел</label>
        </span>
        <span v-if="v$.department_id.$errors?.length || errors?.department_id" class="color-error mt-3">
          {{ errors?.department_id ? errors.department_id[0] : t(v$.department_id.$errors?.[0].$message) }}
        </span>
      </span>
    </MainCard>
  </section>
</template>

<style lang="scss" scoped>
.segments__list {
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 16px;
  row-gap: 30px;
  border-radius: 10px;
  overflow: hidden;
  --gap: 1px;
  --line-offset: calc(var(--gap) / 2);
  --line-thickness: 1px;
  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.modals-item__delete-button {
  border: 1px solid #E26B6B;
  color: #E26B6B;
  background-color: transparent;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  @media screen and (min-width: 1600px) {
    padding: 12px 85.5px;
  }
  &:hover {
    background-color: transparent !important;
  }
}
.modals-item__save-button {
  border: 1px solid #C3987A;
  color: white;
  background-color: #C3987A;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  @media screen and (min-width: 1600px) {
    padding: 12px 35.5px;
  }
  &:hover {
    background-color: #C3987A !important;
  }
}
.modals-item__wrapper {
  display: grid;
  gap: 30px;
  grid-template-columns: 1fr;
  @media screen and (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
::v-deep(.text-3xl) {
  font-weight: 600;
  font-size: 24px !important;
  line-height: 32px;
}
</style>